import { useQuery } from '@tanstack/react-query';
import { AxiosMethods } from '@setvi/shared/enums';
import { axiosHelper } from '@setvi/shared/services';

// v2.0 QR-code endpoint. Hits ADMIN_API_BASE_URL (https://devapi.setvi.com in
// develop). The resource id is appended: `{cartBase}/qrcode/{resourceId}`.
const QR_CODE_ENDPOINT = '/api/v2.0/ShoppingCart/qrcode';

// v2.0 dynamic QR generation endpoint (called on "Generate QR").
const DYNAMIC_QR_CODE_ENDPOINT = '/api/v2.0/ShoppingCart/dynamicqrcode';

enum QrCodeQueryKey {
  Nodes = 'print_order_qr_nodes'
}

// QR node data for a resource. `NodeIds` are the machine numbers configured for
// the resource (e.g. ["3","2","1"]) — used to prefill the QR input.
export interface QrCodeData {
  ResourceId: number;
  HasQRCode: boolean;
  Available: boolean;
  NodeIds: string[] | null;
  RoomID: string | null;
  LocationNumber: string | null;
  Message: string | null;
}

interface QrCodeApiResponse {
  Status: string;
  Data: QrCodeData | null;
  Error: { Id: number; Message: string } | string | null;
}

const getQrCodesQuery = (resourceId: number) => ({
  queryKey: [QrCodeQueryKey.Nodes, resourceId],
  queryFn: async (): Promise<QrCodeData | null> => {
    const response: QrCodeApiResponse = await axiosHelper({
      endpoint: `${QR_CODE_ENDPOINT}/${resourceId}`,
      method: AxiosMethods.GET,
      baseURL: process.env.ADMIN_API_BASE_URL
    });
    return response?.Data ?? null;
  }
});

/**
 * Fetch the QR-code data for a resource. Runs only once `enabled` is true (i.e.
 * the user turned the QR-code toggle on), mirroring `useCoverTemplates`.
 * A resource with no QR nodes returns a 4xx ("DB returned no data") — a normal
 * "nothing available" state, so we don't retry or toast; `qrData` is null.
 */
export const useQrCodes = (resourceId: number, enabled: boolean) => {
  const { data, isLoading } = useQuery({
    ...getQrCodesQuery(resourceId),
    enabled: enabled && !!resourceId,
    retry: false
  });
  return { qrData: data ?? null, loading: isLoading };
};

// Generated dynamic QR data — a single PDF sheet covering the requested nodes.
export interface DynamicQrCodeData {
  Id: number;
  Type: number;
  TypeName: string;
  ResourceId: number;
  FileName: string;
  BlobPath: string;
  DownloadURL: string | null;
  DownloadURLWithSas: string | null;
  CoverPageName: string | null;
  MachineId: string | null;
  QrUrl: string | null;
  CreatedUtc: string;
}

interface DynamicQrCodeApiResponse {
  Status: string;
  Data: DynamicQrCodeData | null;
  Error: { Id: number; Message: string } | string | null;
}

export interface GenerateDynamicQrCodeInput {
  resourceId: number;
  /** Entered machine node ids, e.g. ["1","2","3"]. */
  nodeIds: string[];
}

/**
 * Generate the dynamic QR codes for a resource and the entered machine node ids.
 * NodeIds are sent as an array holding one comma-separated string, e.g.
 * ["3,2,1"].
 */
const generateDynamicQrCode = async ({
  resourceId,
  nodeIds
}: GenerateDynamicQrCodeInput): Promise<DynamicQrCodeData | null> => {
  const response: DynamicQrCodeApiResponse = await axiosHelper({
    endpoint: DYNAMIC_QR_CODE_ENDPOINT,
    method: AxiosMethods.POST,
    baseURL: process.env.ADMIN_API_BASE_URL,
    body: { resourceId, nodeIds: [nodeIds.join(',')] }
  });
  return response?.Data ?? null;
};

// React Query mutation factory (used via useSubscribedMutation, matching the
// app's Services/Mutations convention). Called on "Generate QR".
export const generateDynamicQrCodeMutation = () => ({
  mutationFn: generateDynamicQrCode
});
